import { EventEmitter as EventBus } from '@src/slide/infrastructure/module/independent/event-emitter';
import type { EventMap } from '@src/slide/infrastructure/module/independent/event-emitter/event-emitter.interface';
import { EXTRA_DIV_FOR_ACCESSIBILITY } from './accessibility-constants';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType, TDWFileType } from '../../../../infrastructure/module/dependent/report/offline-data-report/constant';
import i18n from '@i18next/i18n';
import creatEditableDivInBody from './utils';
import { IAccessibility } from '@src/slide/application/service/accessibility-manager/core/accessibility-manager.interface';
import ua from '@tencent/docs-user-agent';

export const SPEAKER_TRIGGER_ID = 'speaker-trigger';
export const ACCESSIBILITY_SWITCHER = 'pc-handler-status-accessibility-';

export enum AccessibilityEventsKeys {
    ACTION_ENABLE = 'isEnable',
}

export interface AccessibilityEventsMap {
    [AccessibilityEventsKeys.ACTION_ENABLE]: boolean;
}

/**
 * 无障碍控制层实现
 * 1.提供一些控制无障碍读屏的能力
 * 2.提供无障碍开关能力
 */
export class AccessibilityRich<AccessibilityEventsMap extends EventMap>
    extends EventBus<EventMap> implements IAccessibility {
    private isEnableState = false;
    private readStrTimer;
    private disableTimer;
    private accessibilityKey = ACCESSIBILITY_SWITCHER;
    private hasFullStop = false;

    public initWithUid(uid: string) {
        this.accessibilityKey += uid;
        this.getLocalAccessibility().then((enableState) => {
            this.changeEnableState(enableState);
        });
        this.addListener();
    }
    /**
     * 开启无障碍化
     */
    public enable() {
        window.log?.info(i18n.t('无障碍开启'));
        Snackbar.show({
            type: 'success',
            message: i18n.t('幻灯片已开启屏幕阅读器支持。'),
            duration: 3000,
        });
        this.changeEnableState(true);
        tdw({
            opername: TDWOperationType.DOC_ALL,
            module: TDWModuleType.DOC_ACCESSIBILITY,
            action: TDWActionType.OPEN,
            file_type: TDWFileType.SLIDE,
        });
    }

    /**
     * 关闭无障碍化
     */
    public disable() {
        window.log?.info(i18n.t('无障碍关闭'));
        Snackbar.show({
            type: 'success',
            message: i18n.t('幻灯片已关闭屏幕阅读器支持。'),
            duration: 3000,
        });
        this.changeEnableState(false);
        tdw({
            opername: TDWOperationType.DOC_ALL,
            module: TDWModuleType.DOC_ACCESSIBILITY,
            action: TDWActionType.CLOSE,
            file_type: TDWFileType.SLIDE,
        });
    }

    public isEnable() {
        return this.isEnableState;
    }

    /**
     * 直接读取某段文字
     * @param contentStr 内容
     * @param force 是否无视开关强制读
     */
    public readStr(contentStr: string, force?: boolean) {
        let triggerDom = document.getElementById(SPEAKER_TRIGGER_ID);
        let needRemoveDomWhenForce = false;
        if (force && !triggerDom) {
            this.loadTriggerAndExtraDom();
            triggerDom = document.getElementById(SPEAKER_TRIGGER_ID);
            needRemoveDomWhenForce = true;
        }
        if (triggerDom) {
            // window.log?.info('readStr=', contentStr);
            this.readStrTimer && clearTimeout(this.readStrTimer);
            this.readStrTimer = setTimeout(() => {
                if (triggerDom) {
                    triggerDom.textContent = `${this.getContentStr(contentStr)}`;
                }
                clearTimeout(this.readStrTimer);
                if (needRemoveDomWhenForce && !this.isEnableState) this.removeDom();
            }, 50);
        }
    }

    private getContentStr(contentStr: string) {
        let tempStr = contentStr;
        if (contentStr) {
            if (this.hasFullStop) {
                tempStr = `${contentStr}。`;
            }
            this.hasFullStop = !this.hasFullStop;
        }
        return tempStr;
    }

    /**
     * 加载触发器dom，以及要读取的额外信息dom
     */
    private loadTriggerAndExtraDom() {
        const speakerTriggerDom = document.getElementById(SPEAKER_TRIGGER_ID);
        if (speakerTriggerDom) return;
        // 创建触发器dom，专门负责读取指定内容
        const triggerDom = document.createElement('div');
        triggerDom.setAttribute('id', SPEAKER_TRIGGER_ID);
        triggerDom.setAttribute('aria-live', 'assertive');
        triggerDom.setAttribute('aria-atomic', 'true');
        triggerDom.setAttribute('aria-hidden', 'false');
        triggerDom.style.cssText = 'position: absolute; left: -999px; top: -1px; height: 0; width: 0;';
        document.body.appendChild(triggerDom);

        this.accessEventFocus();  // 无障碍事件聚焦
        // window.log?.info(i18n.t('无障碍加载触发器'));
    }

    /**
     * 把事情从读屏软件中拿过来
     */
    private accessEventFocus() {
        const editor = creatEditableDivInBody();
        editor.setAttribute('class', EXTRA_DIV_FOR_ACCESSIBILITY);
        document.body.append(editor);
        editor.focus();
        setTimeout(() => {
            editor.remove();
        }, 5000);
    }

    /**
     * 移除动态添加的dom
     */
    private removeDom() {
        const triggerDom = document.getElementById(SPEAKER_TRIGGER_ID);
        triggerDom?.remove();
    }

    private changeEnableState(enableState: boolean) {
        this.setLocalAccessibility(enableState);
        this.isEnableState = enableState;
        if (enableState) {
            this.disableTimer && clearTimeout(this.disableTimer);
            this.loadTriggerAndExtraDom();
        } else {
            this.disableTimer = setTimeout(() => {
                this.removeDom();
                clearTimeout(this.disableTimer);
            }, 50);
        }
        this.emit(AccessibilityEventsKeys.ACTION_ENABLE, enableState);
    }

    private getLocalAccessibility(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            try {
                if (this.isTencentDesktop()) {
                    window.mqq.invoke('storage', 'setting', { action: 'get',  key: 'enableAccessibility' }, (result) => {
                        resolve(result.data);
                    });
                } else {
                    resolve(localStorage.getItem(this.accessibilityKey) === '1');
                }
            } catch (error) {
                reject(error);
            }
        });
    }

    private setLocalAccessibility(enableState: boolean) {
        if (this.isTencentDesktop()) {
            window.mqq.invoke('storage', 'setting', { action: 'set',  key: 'enableAccessibility', value: enableState });
        } else {
            localStorage.setItem(this.accessibilityKey, enableState ? '1' : '0');
        }
    }

    /**
     * 监听无障碍状态变更
     */
    private addListener() {
        if (this.isTencentDesktop()) {
            const subscription = window.mqq.createSubscribe('enableAccessibility');
            subscription.subscribe((value) => {
                this.changeEnableState(value);
            });
        } else {
            window.addEventListener('storage', (event: StorageEvent) => {
                if (event.storageArea === localStorage) {
                    if (event.key === this.accessibilityKey) {
                        this.changeEnableState(event.newValue === '1');
                    }
                }
            });
        }
    }

    /**
     * 当为桌面端，且版本号大于等于3.9.5时，需要取桌面端的无障碍设置
     */
    private isTencentDesktop() {
        return ua.isElectronTencentDocsClient && ua.isElectronTencentDocsClientVersionAboveOrEqual('3.9.5') && window.mqq;
    }
}

const Accessibility = new AccessibilityRich();
export default Accessibility;
